interface ArticleBodyProps {
  /** Raw article content (markdown / plain text) — used as fallback and
   *  as the source of truth in editor-preview contexts where there's no
   *  prepared_content yet. */
  content: string;
  /** Pipeline-prepared content with inline `<figure>` HTML for images.
   *  When present takes precedence over `content` for rendering. */
  preparedContent?: string | null;
  /** Featured/hero images from `articles.prepared_images`. The first
   *  `role === 'featured'` item is rendered above the body. */
  preparedImages?: Array<Record<string, any>> | null;
  /** Legacy image metadata from `articles.article_metadata.images`,
   *  used when prepared_content is absent. */
  legacyImages?: Array<Record<string, any>> | null;
}

/**
 * Shared article body renderer used by:
 * - The Article detail page read view (full prepared_content + images).
 * - The Markdown editor's preview pane (raw content, no images).
 *
 * Same render path either way so editor preview and live view never
 * drift.
 */
export function ArticleBody({
  content,
  preparedContent,
  preparedImages,
  legacyImages,
}: ArticleBodyProps) {
  const featured = preparedImages?.find((img) => img.role === 'featured');

  return (
    <div
      className="max-w-none"
      style={{
        fontFamily: "'Manrope', system-ui, -apple-system, sans-serif",
        lineHeight: '1.75',
      }}
    >
      {preparedContent ? (
        <>
          {featured && (
            <div className="mb-8 flex justify-center">
              <div className="w-full max-w-3xl">
                <img
                  src={featured.hero_url || featured.inline_url || featured.url}
                  alt={featured.alt || ''}
                  className="w-full rounded-lg shadow-lg border border-grey-200"
                  loading="lazy"
                />
                {featured.caption && (
                  <p className="text-sm text-grey-600 mt-2 text-center italic">
                    {featured.caption}
                  </p>
                )}
              </div>
            </div>
          )}
          {preparedContent.split('\n\n').map((block, index) => {
            const trimmed = block.trim();
            if (!trimmed) return null;

            if (trimmed.startsWith('<figure')) {
              return (
                <div
                  key={index}
                  className="my-8 flex justify-center [&_figure]:w-full [&_figure]:max-w-3xl [&_img]:w-full [&_img]:rounded-lg [&_img]:shadow-lg [&_img]:border [&_img]:border-grey-200 [&_figcaption]:text-sm [&_figcaption]:text-grey-600 [&_figcaption]:mt-2 [&_figcaption]:text-center [&_figcaption]:italic"
                  dangerouslySetInnerHTML={{ __html: trimmed }}
                />
              );
            }

            if (index === 0) {
              return (
                <p
                  key={index}
                  className="text-xl leading-relaxed text-grey-800 font-medium mb-8 first-letter:text-3xl first-letter:font-bold first-letter:text-primary-700"
                >
                  {trimmed}
                </p>
              );
            }

            return (
              <p key={index} className="mb-6 text-grey-800 text-lg leading-relaxed">
                {trimmed}
              </p>
            );
          })}
        </>
      ) : (
        // Legacy fallback: no prepared_content. Render paragraphs and
        // optionally interleave images at fixed positions.
        //
        // Image source priority:
        //   1. preparedImages with role='inline' (post-reselect / post-undo /
        //      post-redo state — prepared_content was nulled, but the
        //      curated selection is still in prepared_images)
        //   2. legacyImages (article_metadata.images — raw unfiltered set,
        //      includes PDF page screenshots and other junk that the
        //      selection rules already rejected)
        //
        // Without this priority, the manager preview after a reselect
        // briefly showed the raw legacy images (page screenshots, junk)
        // before the publishing service had time to re-render the test
        // build. E25 article (c8157afd-…) 2026-06-23 trigger case.
        content.split('\n\n').map((paragraph, index) => {
          const preparedInline = (preparedImages || []).filter(
            (img: Record<string, any>) => img?.role === 'inline',
          );
          const images: Array<Record<string, any>> = preparedInline.length > 0
            ? preparedInline
            : (legacyImages || []);
          const hasImages = images.length > 0;
          const paragraphCount = content.split('\n\n').length;
          const firstImagePosition = 1;
          const secondImagePosition = Math.floor(paragraphCount / 2);

          let imageToShow: any = null;
          if (hasImages && index === firstImagePosition && images[0]) {
            imageToShow = images[0];
          } else if (hasImages && index === secondImagePosition && images[1]) {
            imageToShow = images[1];
          }

          return (
            <div key={index}>
              {index === 0 ? (
                <p className="text-xl leading-relaxed text-grey-800 font-medium mb-8 first-letter:text-3xl first-letter:font-bold first-letter:text-primary-700">
                  {paragraph}
                </p>
              ) : (
                <p className="mb-6 text-grey-800 text-lg leading-relaxed">
                  {paragraph}
                </p>
              )}

              {imageToShow && (
                <div className="my-8 flex justify-center">
                  <div className="w-full max-w-3xl">
                    <img
                      src={
                        // prepared_images shape (post-reselect): inline_url
                        // at root. legacyImages shape (article_metadata):
                        // variants[].cdn_url + cdn_url fallback.
                        imageToShow.inline_url ||
                        imageToShow.variants?.find((v: any) => v.variant_type === 'inline')?.cdn_url ||
                        imageToShow.cdn_url ||
                        imageToShow.url
                      }
                      alt={
                        imageToShow.alt ||
                        imageToShow.ai_description?.suggested_alt_text ||
                        imageToShow.caption ||
                        imageToShow.alt_text ||
                        `Image from page ${imageToShow.page_number || 'unknown'}`
                      }
                      className="w-full rounded-lg shadow-lg border border-grey-200"
                      loading="lazy"
                    />
                    <p className="text-sm text-grey-600 mt-2 text-center italic">
                      {imageToShow.caption ||
                        imageToShow.ai_description?.suggested_caption ||
                        `${
                          (imageToShow.image_type || 'image').charAt(0).toUpperCase() +
                          (imageToShow.image_type || 'image').slice(1).replace(/_/g, ' ')
                        } from source document`}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
